package com.kanati.admin.features.reports

import com.kanati.admin.api.AdminApiException
import com.kanati.shared.commerce.ConcessionOrderDto
import com.kanati.shared.commerce.ConcessionOrderSource
import com.kanati.shared.commerce.ConcessionOrderStatus

val activeConcessionStatuses = listOf(
    ConcessionOrderStatus.RECEIVED, ConcessionOrderStatus.IN_PREP, ConcessionOrderStatus.READY,
)

fun concessionStatusLabel(status: ConcessionOrderStatus): String = when (status) {
    ConcessionOrderStatus.RECEIVED -> "Received · awaiting prep"
    ConcessionOrderStatus.IN_PREP -> "In prep"
    ConcessionOrderStatus.READY -> "Ready · awaiting pickup"
    ConcessionOrderStatus.COMPLETED -> "Completed · handed to customer"
    ConcessionOrderStatus.CANCELED -> "Canceled · fulfillment stopped"
}

fun concessionSourceLabel(source: ConcessionOrderSource): String = when (source) {
    ConcessionOrderSource.POS_COUNTER -> "Counter"
    ConcessionOrderSource.CONSUMER_MOBILE -> "Mobile"
}

const val concessionReviewNotice = "Read-only review and reconciliation. POS Android Kitchen mode handles active preparation and pickup. Snapshots refresh manually; refresh before reconciling."
const val concessionFinancialNotice = "Fulfillment status changes create no sales, inventory changes, or ledger rows. Cancellation does not refund, reverse a sale, or restore stock. Use sales and ledger records for financial reconciliation."
const val concessionMobileNotice = "Consumer mobile concessions remain deferred and are not launch-ready. A Mobile source label does not enable Consumer checkout."
const val concessionListLimit = "Active orders only, oldest first. Completed and canceled orders are available by order or sale ID lookup below. Date-range filtering and terminal-order history are not available."

fun concessionLineSummary(order: ConcessionOrderDto): String = order.lineItems.joinToString("; ") {
    "${it.quantity} × ${it.productNameSnapshot}"
}

fun concessionOrderFacts(order: ConcessionOrderDto): List<Pair<String, String>> = listOf(
    "Order ID" to order.id,
    "Sale reference" to order.saleId,
    "Pickup / display number" to order.displayNumber,
    "Source" to concessionSourceLabel(order.source),
    "Fulfillment status" to concessionStatusLabel(order.status),
    "Pickup name" to (order.pickupName?.takeIf(String::isNotBlank) ?: "Not supplied"),
    "Created (UTC)" to order.createdAt,
    "Updated (UTC)" to order.updatedAt,
    "Ready (UTC)" to (order.readyAt ?: "Not recorded"),
    "Completed (UTC)" to (order.completedAt ?: "Not recorded"),
    "Canceled timestamp" to "Not supplied separately",
    "Cancellation reason" to (order.cancellationReason ?: "Not supplied"),
)

/** Only an authoritative 404 is absence. Auth, transport, and decoding failures propagate. */
suspend fun findConcessionOrder(read: suspend () -> ConcessionOrderDto): ConcessionOrderDto? = try {
    read()
} catch (failure: AdminApiException) {
    if (failure.statusCode == 404) null else throw failure
}

fun ConcessionOrderDto.requireReviewScope(districtId: String, schoolId: String): ConcessionOrderDto {
    require(this.districtId == districtId && this.schoolId == schoolId) { "Order scope does not match selected school" }
    return this
}
